import { Injectable } from "@angular/core";
import {
  ExportService,
  ExportFileFormat,
  ExportRange,
  ExportContext,
} from "app/layout/export-overlay";
import { takeUntil } from "rxjs/operators";
import ceil from "lodash-es/ceil";
import get from "lodash-es/get";
import { ClientMaterialSummary, MaterialSummary, GroupedMaterialWithCustomers } from "../interfaces/material-group-partner";
import { PrincipalScreenService } from "../../principal-screen/principal-screen.service";
import { IPagination } from "app/common/interfaces";
import { OrderAttributes } from "@albatrosdeveloper/ave-models-package";
import { MatSnackBar } from "@angular/material/snack-bar";

export const CLIENT_MATERIAL_EXPORT_ID = "client-material-summary";
export const CLIENT_MATERIAL_TITLE = "Materiales por Cliente";

export const CLIENT_MATERIAL_PROPERTIES = {
  clientCode: "Código Cliente",
  clientName: "Nombre Cliente", 
  materialCode: "Código Material",
  materialName: "Nombre Material",
  totalBaseQuantity: "Cantidad UMB",
  baseUnitOfMeasureName: "Unidad de Medida UMB",
  totalParallelQuantity: "Cantidad UMP", 
  parallelUnitOfMeasureName: "Unidad de Medida UMP",
  materialsTotalPrice: "Total"
};

@Injectable()
export class ClientMaterialExport {
  constructor(
    private exportService: ExportService,
    private orderService: PrincipalScreenService,
    private snackBar: MatSnackBar
  ) {}

  /**
   * Transforms grouped materials with customers to client materials format for export consistency
   */
  private transformGroupedMaterialsToClientMaterials(groupedMaterials: GroupedMaterialWithCustomers[]): ClientMaterialSummary[] {
    const result: ClientMaterialSummary[] = [];
    
    groupedMaterials.forEach(material => {
      material.businessPartners.forEach(partner => {
        // Create a ClientMaterialSummary object for each business partner
        const clientMaterial: ClientMaterialSummary = {
          id: partner.shipToMongoId,
          orderCount: partner.ordersCount,
          clientName: partner.shipToName,
          clientCode: partner.shipToExternalKeys,
          shipTo: {
            _id: partner.shipToMongoId,
            externalKeys: {
              code: partner.shipToExternalKeys
            },
            name: partner.shipToName
          },
          soldTo: {
            _id: partner.shipToMongoId,
            externalKeys: {
              code: partner.shipToExternalKeys
            },
            name: partner.shipToName
          },
          materialsTotalPrice: partner.soldQuantity || 0,
          materials: [{
            materialCode: material.code,
            materialName: material.name,
            totalBaseQuantity: partner.baseQuantity,
            baseUnitOfMeasureCode: material.baseUnitOfMeasure.code,
            baseUnitOfMeasureName: material.baseUnitOfMeasure.name,
            totalParallelQuantity: partner.pararellQuantity,
            parallelUnitOfMeasureCode: material.pararellUnitOfMeasure.code,
            parallelUnitOfMeasureName: material.pararellUnitOfMeasure.name,
            materialsTotalPrice: partner.soldQuantity || 0
          }]
        };
        result.push(clientMaterial);
      });
    });

    return result;
  }

  private transformClientMaterials(clients: ClientMaterialSummary[]) {
    const result: any[] = [];
        
    clients.forEach((client) => {
      if (client.materials && client.materials.length > 0) {
        client.materials.forEach((material: MaterialSummary) => {
          result.push({
            clientCode: client.clientCode || '',
            clientName: client.clientName || '',
            materialCode: material.materialCode || '',
            materialName: material.materialName || '',
            totalBaseQuantity: material.totalBaseQuantity || 0,
            baseUnitOfMeasureName: material.baseUnitOfMeasureName || '',
            totalParallelQuantity: material.totalParallelQuantity || 0,
            parallelUnitOfMeasureName: material.parallelUnitOfMeasureName || '',
            materialsTotalPrice: material.materialsTotalPrice || 0
          });
        });
      }
    });
    
    return result;
  }

  export(
    format: ExportFileFormat,
    range: ExportRange,
    totalItems: number,
    pageSize: number,
    pageIndex: number,
    tripId: number,
    documentTypes: string[]
  ) {
    const totalPages = ceil(totalItems / pageSize);
    
    this.exportService
      .task({
        id: CLIENT_MATERIAL_EXPORT_ID,
        title: CLIENT_MATERIAL_TITLE,
        format: format,
        iterations: range === ExportRange.AllPages ? totalPages : 1,
        start: (ctx: ExportContext) => {
          ctx.processor.localizeItemProperties(
            CLIENT_MATERIAL_TITLE,
            CLIENT_MATERIAL_PROPERTIES
          );
        },
        next: (ctx: ExportContext) => {
          const currentPage = range === ExportRange.AllPages ? ctx.iteration : pageIndex;
          const isAllPages = range === ExportRange.AllPages;
          
          const paginationOptions: IPagination<OrderAttributes> = {
            currentPage: isAllPages ? 1 : currentPage + 1,
            limitPerPage: isAllPages ? Number.MAX_SAFE_INTEGER : pageSize,
          };
          
          this.orderService.getGroupedMaterialsByCustomers(
            paginationOptions,
            tripId,
            documentTypes
          ).pipe(takeUntil(ctx.stopped)).subscribe((response) => {
            const groupedMaterials = get(response, 'data.0.data', []);
            // Transform grouped materials back to client materials format for consistent export
            const clientMaterials = this.transformGroupedMaterialsToClientMaterials(groupedMaterials);
            const transformedData = this.transformClientMaterials(clientMaterials);
            
            ctx.processor.addToSection(
              CLIENT_MATERIAL_TITLE,
              transformedData
            );
            
            ctx.next();
          }, (error) => {
            this.snackBar.open(error.error.message || "Error al obtener grupo de materiales por cliente.","Cerrar",{ duration: 5000});
          });
        },
      })
      .start();
  }

  /**
   * Método para exportar un cliente específico
   */
  exportSingleClient(
    format: ExportFileFormat,
    client: ClientMaterialSummary
  ) {
    this.exportService
      .task({
        id: `${CLIENT_MATERIAL_EXPORT_ID}-single`,
        title: `${CLIENT_MATERIAL_TITLE} - ${client.clientName}`,
        format: format,
        iterations: 1,
        start: (ctx: ExportContext) => {
          ctx.processor.localizeItemProperties(
            CLIENT_MATERIAL_TITLE,
            CLIENT_MATERIAL_PROPERTIES
          );
        },
        next: (ctx: ExportContext) => {
          const transformedData = this.transformClientMaterials([client]);
          
          ctx.processor.addToSection(
            CLIENT_MATERIAL_TITLE,
            transformedData
          );
          
          ctx.next();
        },
      })
      .start();
  }

  canStartTask() {
    if (this.exportService.taskIsActive(CLIENT_MATERIAL_EXPORT_ID)) {
      this.exportService.setVisible(true);
      return false;
    }
    
    return true;
  }
}